import React, { useState } from 'react';
import { useDecimalDraft } from './useDecimalDraft.ts';

interface PercentageInputProps {
    label?: React.ReactNode;
    name?: string;
    value: number | string | null | undefined;
    onChange: (value: number | undefined) => void;
    className?: string;
    placeholder?: string;
    id?: string;
    disabled?: boolean;
}

/**
 * Numeric input for rates/percentages (growth rates, interest rates, franking, apportionment — the
 * percentage counterpart to CurrencyInput; see NumberInput for plain counts/years). Uses a text
 * input rather than type="number" so a stray '$' actually reaches us: that means the user has
 * confused a percentage field for a dollar one, so we reject the keystroke and flag the mistake
 * rather than let the browser silently swallow it. A trailing '%' the user types is tolerated.
 */
const PercentageInput = ({ label, name, value, onChange, className, placeholder, id, disabled }: PercentageInputProps) => {
    const [error, setError] = useState('');
    // Shows what the user is typing while they type it, so a decimal point survives — see the hook.
    const { displayValue, setDraft, syncOnBlur } = useDecimalDraft(value);
    /*
     * Shown as "9%" once the field is left, the way a dollar field shows "$15,000" (owner, 15 Oct
     * 2026). While it has focus the bare number is shown, so typing and deleting behave as before; the
     * change handler already strips a '%' the user types.
     */
    const [focused, setFocused] = useState(false);
    const shownValue = !focused && displayValue !== '' ? `${displayValue}%` : displayValue;
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const input = e.target.value;
        if (input.includes('$')) {
            setError('This is a percentage, not a dollar amount — remove the "$".');
            return;
        }
        setError('');
        // Tolerate a trailing '%' the user tacks on, plus any stray whitespace.
        const cleaned = input.replace(/[%\s]/g, '');
        // Kept AFTER the '$' rejection above, so a rejected keystroke is never held as the draft.
        setDraft(cleaned);
        if (cleaned === '') {
            onChange(undefined);
            return;
        }
        const parsed = parseFloat(cleaned);
        onChange(Number.isNaN(parsed) ? undefined : parsed);
    };
    return (
        <div className={className}>
            {label && <label htmlFor={id} className="block text-sm font-medium text-gray-700 mb-1">{label}</label>}
            <input
                type="text"
                inputMode="decimal"
                id={id}
                name={name}
                value={shownValue}
                onChange={handleChange}
                onFocus={() => setFocused(true)}
                onBlur={() => { setFocused(false); syncOnBlur(); }}
                placeholder={placeholder}
                disabled={disabled}
                className="w-full px-4 py-2 bg-white/80 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 transition disabled:bg-gray-100 disabled:cursor-not-allowed"
            />
            {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
        </div>
    );
};

export default PercentageInput;
